import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { EVENTS } from '@flows/configs/analytics/constants'
import type {
  CoveragePlan,
  PriceConfiguration,
  ProductContentMainGroup,
  ProductContentSection,
  ProductExtension,
} from '@flows/index'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import Spacer from '@ui-kit/components/layoutComponents/Spacer'
import { h3, h4 } from '@ui-kit/core/typography'

import { CmsIcon } from './CmsIcon'
import { SubGroupAccordion } from './SubGroupAccordion'
import { quoteSectionRow } from './sectionRowStyles'

const rowStyles = quoteSectionRow()

const coverageSectionStyles = tv({
  slots: {
    backdrop: 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/50',
    modal: 'mx-2 max-h-[85vh] w-[96vw] max-w-lg overflow-hidden rounded-2xl bg-white',
    scroll: 'px-5 py-4',
    mainGroupWrapper: 'pt-2',
    mainGroupHeader: 'flex-row items-center justify-between',
    subGroupsWrapper: 'pl-4',
  },
})

type Props = {
  selectedCoveragePlan: CoveragePlan
  configurationParameters?: PriceConfiguration | Record<string, unknown>
  extensions?: ProductExtension[]
}

const CoverageSectionModal = ({
  section,
  onClose,
}: {
  section: ProductContentSection
  onClose: () => void
}) => {
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const styles = coverageSectionStyles()

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  return createPortal(
    <div
      role="button"
      tabIndex={0}
      className={styles.backdrop()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }}
    >
      <View className={styles.modal()}>
        <ScrollView className={styles.scroll()}>
          {section.contentMainGroups?.map((mainGroup, groupIndex) => {
            const mainGroupKey = mainGroup.itemKey?.current ?? mainGroup.title
            const hasSingleSubGroup = mainGroup.contentSubGroups?.length === 1
            return (
              <View key={mainGroupKey} className={styles.mainGroupWrapper()}>
                <View className={styles.mainGroupHeader()}>
                  <Text className={h3()}>{mainGroup.title}</Text>
                  {groupIndex === 0 && (
                    <Pressable onPress={onClose}>
                      <PhosphorIcon name="X" size={24} />
                    </Pressable>
                  )}
                </View>
                <Spacer size="small" />
                <View className={styles.subGroupsWrapper()}>
                  {mainGroup.contentSubGroups?.map((subGroup) => (
                    <SubGroupAccordion
                      key={subGroup.itemKey?.current ?? subGroup.title}
                      subGroup={subGroup}
                      expandedItem={expandedItem}
                      onToggleItem={setExpandedItem}
                      flat={hasSingleSubGroup}
                      defaultExpanded
                    />
                  ))}
                </View>
              </View>
            )
          })}
        </ScrollView>
      </View>
    </div>,
    document.body
  )
}

export const CoverageSection = (props: Props) => {
  const { t } = useTranslation()
  const { trackEvent } = useQuoteData()
  const [openSection, setOpenSection] = useState<ProductContentSection | null>(null)
  const styles = rowStyles

  const openCoverageSection = (section: ProductContentSection) => {
    setOpenSection(section)
    trackEvent(EVENTS.PopupOpened, {
      id: `coverage_section_${section.itemKey?.current ?? section.title}`,
    })
  }

  const selectedExtensions = props.extensions
    ? props.extensions.filter(
        (ext) =>
          props.configurationParameters?.[
            ext.configurationDataExtensionKey as keyof typeof props.configurationParameters
          ]
      )
    : []

  const baseSections = props.selectedCoveragePlan.contentSections ?? []
  const extensionSection: ProductContentSection | null =
    selectedExtensions.length > 0
      ? {
          _type: 'productContentSection',
          title: t('sharedFlows_common:steps.quote.yourCoverage.extensions.title'),
          itemKey: { _type: 'extensionSection', current: 'extensionsCoverageSection' },
          contentMainGroups: selectedExtensions.map<ProductContentMainGroup>((ext) => ({
            _type: 'productContentMainGroup',
            title: ext.title,
            contentSubGroups: ext.contentSubGroups,
            itemKey: { _type: 'uniqueId', current: ext.configurationDataExtensionKey },
          })),
          icon: { small: { name: 'shieldWithCheckmark_24' } },
        }
      : null
  const contentSections = extensionSection ? [...baseSections, extensionSection] : baseSections

  return (
    <View className={styles.container()}>
      <Text className={h3()}>{t('sharedFlows_common:steps.quote.yourCoverage.title')}</Text>
      <Spacer size="small" />
      {contentSections.map((section, index) => {
        const sectionKey = section.itemKey?.current ?? section.title

        return (
          <React.Fragment key={sectionKey}>
            <Pressable className={styles.row()} onPress={() => openCoverageSection(section)}>
              <View className={styles.left()}>
                <CmsIcon name={section.icon?.small?.name} />
                <Text className={h4()}>{section.title}</Text>
              </View>
              <PhosphorIcon name="CaretRight" size={24} />
            </Pressable>
            {index + 1 !== contentSections.length && <View className={styles.divider()} />}
          </React.Fragment>
        )
      })}

      {openSection && (
        <CoverageSectionModal section={openSection} onClose={() => setOpenSection(null)} />
      )}
    </View>
  )
}
